/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import PropTypes from 'prop-types';
import { ChevronRight } from '@carbon/react/icons';
import NavItem from './NavItem.js';
import { isActiveItem } from '../utils/findActiveItem.js';

var _excluded = ["onClick", "onKeyDown", "href", "to", "label"];
var NavFlyout = function NavFlyout(_ref) {
  var activeHref = _ref.activeHref,
    id = _ref.id,
    items = _ref.items,
    menuButtonLabel = _ref.label,
    _ref$menuButtonCompon = _ref.menuButtonComponent,
    MenuButtonComponent = _ref$menuButtonCompon === void 0 ? 'button' : _ref$menuButtonCompon,
    onItemSelect = _ref.onItemSelect,
    _ref$linkComponent = _ref.linkComponent,
    LinkComponent = _ref$linkComponent === void 0 ? 'a' : _ref$linkComponent,
    Icon = _ref.icon;
  var flyoutButtonRef = React.useRef(null);
  var flyoutContentRef = React.useRef(null);
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    position = _React$useState2[0],
    setPosition = _React$useState2[1];
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    boxPosition = _React$useState4[0],
    setBoxPosition = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    hover = _React$useState6[0],
    setHover = _React$useState6[1];
  React.useLayoutEffect(function () {
    if (flyoutButtonRef.current) {
      var _findNavItemsComponents = function findNavItemsComponents(element) {
        if ((element === null || element === void 0 ? void 0 : element.className) === 'cpc--side-nav__items') {
          return element;
        }
        if (element.parentElement && element.parentElement.nodeName !== 'NAV') {
          return _findNavItemsComponents(element.parentElement);
        }
        return undefined;
      };
      var parent = _findNavItemsComponents(flyoutButtonRef.current);
      var parentScroll = parent ? parent.scrollTop : 0;
      // We need to manually position the flyout since it is absolutely positioned relative to the whole Nav element (versus button)
      // Due to how we need to preserve the overflow.
      // First we get the height offset of the flyout menu (the button). Then we substract the scroll position of the container if there is any.
      // The resulting position would position the flyout's top to be aligned with the button's top. However if the flyout is large this might
      // not look as good, so we go a step further and we move the flyout up so that its center is lined up with the menu instead, by
      // adding half of the flyout height, and half of the flyout menu button height.
      var centeredPosition = flyoutButtonRef.current.offsetTop - parentScroll;
      setPosition(centeredPosition);
      setBoxPosition(flyoutButtonRef.current.offsetTop - parentScroll);
    }
  }, [position, hover]);
  var iconItem = !Icon ? null : /*#__PURE__*/React.createElement(Icon, {
    "aria-label": "icon",
    className: "cpc--side-nav__item-icon",
    "aria-hidden": "true"
  });
  return /*#__PURE__*/React.createElement("li", {
    className: "cpc--side-nav__item"
  }, /*#__PURE__*/React.createElement(MenuButtonComponent, {
    "aria-haspopup": "true",
    ref: flyoutButtonRef,
    onMouseOver: function onMouseOver() {
      return setHover(true);
    },
    onMouseOut: function onMouseOut() {
      return setHover(false);
    },
    onFocus: function onFocus() {
      return setHover(true);
    },
    onBlur: function onBlur() {
      return setHover(false);
    },
    type: "button",
    className: "cpc--side-nav__menu-button cpc--side-nav__menu-flyout",
    id: id
  }, /*#__PURE__*/React.createElement("div", {
    className: "cpc--side-nav__menu-button__label"
  }, iconItem, menuButtonLabel), /*#__PURE__*/React.createElement(ChevronRight, {
    className: "cpc--side-nav__menu-icon"
  }), /*#__PURE__*/React.createElement("span", {
    className: "cpc--side-nav__flyout-box",
    style: {
      top: boxPosition
    }
  }), /*#__PURE__*/React.createElement("ul", {
    ref: flyoutContentRef,
    className: "cpc--side-nav__flyout",
    style: {
      top: position
    }
  }, items.map(function (item) {
    var onItemClick = item.onClick,
      onItemKeyDown = item.onKeyDown,
      href = item.href,
      to = item.to,
      label = item.label,
      props = _objectWithoutProperties(item, _excluded);
    return /*#__PURE__*/React.createElement(NavItem, _extends({
      key: "".concat(href, "-").concat(label),
      active: isActiveItem(item, activeHref),
      linkComponent: href ? 'a' : LinkComponent,
      label: label,
      to: to,
      href: href,
      onClick: function onClick(evt) {
        return onItemSelect(evt, to || href, onItemClick);
      },
      onKeyDown: function onKeyDown(evt) {
        return onItemSelect(evt, to || href, onItemKeyDown);
      }
      // eslint-disable-next-line react/jsx-props-no-spreading
    }, props));
  }))));
};
NavFlyout.propTypes = {
  /**
   * The href of the nav item that should be active.
   */
  activeHref: PropTypes.string.isRequired,
  /**
   * A unique identifier to apply to the nav flyout element.
   */
  id: PropTypes.string,
  /**
   * The label to assign to the nav flyout element.
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The component to use for link elements. Useful for when using React Router.
   */
  linkComponent: PropTypes.elementType,
  /**
   * A menu button component to render instead of the button. Useful if teams need to intercept
   * the menu button to render a separate component in its place, such as when nesting including
   * interactive content within the menu.
   */
  menuButtonComponent: PropTypes.elementType,
  /**
   * The event to fire off when selecting a navigation item.
   */
  onItemSelect: PropTypes.func.isRequired,
  /**
   * The flyout nested items to pass in for the flyout to render.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string,
    label: PropTypes.string
  })).isRequired,
  /**
   * Icon of the item
   */
  icon: NavItem.propTypes.icon
};
var NavFlyout$1 = NavFlyout;

export { NavFlyout$1 as default };
